import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconBrandOauth, IconDatabase, IconServer } from '@tabler/icons-react';
import '@fontsource-variable/geist/wght.css';
import { hasGoAppLicense } from '../../utils/userFinanceiroAccess';
import { getApiBaseUrl } from '../../utils/apiConfig';
import '../users-customers/components/UsersCustomersView.css';
import PainelControleOauthConfig from './components/PainelControleOauthConfig';
import PainelControleStorageConfig from './components/PainelControleStorageConfig';
import PainelControleDbVervifyConfig from './components/PainelControleDbVervifyConfig';
import { useConfigSectionParam } from '../config-hub';
import ConfigSectionSidebarEdge from '../config-hub/ConfigSectionSidebarEdge';

function readSessionUser() {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

function canShowStorageMenu(user) {
  if (!user) return false;
  const hasCustomers = Number(user.verve_customers_module) === 1;
  const hasGo = hasGoAppLicense(user);
  return hasCustomers || hasGo;
}

function dbVervifyCacheKey() {
  try {
    const payload = localStorage.getItem('authToken')?.split('.')[1];
    const clientId = payload ? JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).clientId : null;
    return clientId ? `pcDbVervifyEligible:${clientId}` : null;
  } catch (_) {
    return null;
  }
}

function readDbVervifyCache(key) {
  const raw = key ? sessionStorage.getItem(key) : null;
  return raw === '1' ? true : raw === '0' ? false : null;
}

function SidebarTooltipPortal({ anchorRect, text }) {
  if (!anchorRect || !text) return null;
  return createPortal(
    <span
      className="process-sidebar-tooltip-portal"
      style={{
        top: anchorRect.top + anchorRect.height / 2,
        left: anchorRect.right + 10,
      }}
      role="tooltip"
    >
      {text}
    </span>,
    document.body
  );
}

export default function PainelControleView({ currentUser: currentUserProp }) {
  const sessionUser = currentUserProp || readSessionUser();
  const showStorage = canShowStorageMenu(sessionUser);
  const dbVervifyKey = useMemo(dbVervifyCacheKey, []);
  const [dbVervifyEligible, setDbVervifyEligible] = useState(() => readDbVervifyCache(dbVervifyKey));
  const showDbVervify = dbVervifyEligible === true;

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/api/config/db-vervify/eligibility`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((result) => {
        if (cancelled) return;
        const eligible = Boolean(result?.eligible);
        setDbVervifyEligible(eligible);
        if (dbVervifyKey) sessionStorage.setItem(dbVervifyKey, eligible ? '1' : '0');
      })
      .catch(() => {
        if (!cancelled) setDbVervifyEligible((prev) => prev === true);
      });
    return () => {
      cancelled = true;
    };
  }, [dbVervifyKey]);

  const moduleSections = useMemo(() => {
    const sections = [
      {
        title: 'Autenticação',
        items: [{ id: 'oauth', label: 'Autenticação OAuth', Icon: IconBrandOauth }],
      },
    ];
    if (showStorage) {
      sections.push({
        title: 'Infraestrutura',
        items: [{ id: 'storage', label: 'Armazenamento', Icon: IconServer }],
      });
    }
    if (showDbVervify) {
      sections.push({
        title: 'Banco de dados',
        items: [{ id: 'db-vervify', label: 'DB Vervify', Icon: IconDatabase }],
      });
    }
    return sections;
  }, [showStorage, showDbVervify]);

  const allowedModules = useMemo(
    () => ['oauth', ...(showStorage ? ['storage'] : []), ...(dbVervifyEligible !== false ? ['db-vervify'] : [])],
    [showStorage, dbVervifyEligible]
  );
  const [activeModule, setActiveModule] = useConfigSectionParam('oauth', allowedModules);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tooltip, setTooltip] = useState({ text: '', rect: null });
  const tipTimer = useRef(null);

  useEffect(() => {
    if (!showStorage && activeModule === 'storage') {
      setActiveModule('oauth');
    }
  }, [showStorage, activeModule, setActiveModule]);

  const showTip = useCallback((e, text) => {
    if (!sidebarCollapsed || !text) return;
    const rect = e.currentTarget.getBoundingClientRect();
    clearTimeout(tipTimer.current);
    tipTimer.current = setTimeout(() => setTooltip({ text, rect }), 80);
  }, [sidebarCollapsed]);

  const hideTip = useCallback(() => {
    clearTimeout(tipTimer.current);
    setTooltip({ text: '', rect: null });
  }, []);

  return (
    <div className="process-config-root">
      <aside
        className={`process-sidebar${sidebarCollapsed ? ' is-collapsed' : ''}`}
        aria-label="Módulos Painel de controle"
      >
        <ConfigSectionSidebarEdge
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((v) => !v)}
        />
        <nav className="process-submenu">
          {moduleSections.map((section) => (
            <div key={section.title} className="customers-submenu-section">
              <div className="customers-submenu-section-title" aria-hidden={sidebarCollapsed}>
                {section.title}
              </div>
              {section.items.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={[
                    'process-submenu-item',
                    activeModule === id ? 'active' : '',
                    sidebarCollapsed ? 'customers-sidebar-tooltip-host' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setActiveModule(id)}
                  onMouseEnter={(e) => showTip(e, label)}
                  onMouseLeave={hideTip}
                  data-tooltip={sidebarCollapsed ? label : undefined}
                  aria-label={sidebarCollapsed ? label : undefined}
                >
                  <Icon size={17} stroke={1.75} aria-hidden />
                  <span className="process-submenu-label">{label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <div
        className={[
          'process-content-area',
          'process-content-area-config-status-2',
          'customers-module-content',
        ].join(' ')}
      >
        {activeModule === 'oauth' && <PainelControleOauthConfig />}
        {activeModule === 'storage' && showStorage ? <PainelControleStorageConfig /> : null}
        {activeModule === 'db-vervify' && showDbVervify ? <PainelControleDbVervifyConfig /> : null}
      </div>

      <SidebarTooltipPortal anchorRect={tooltip.rect} text={tooltip.text} />
    </div>
  );
}
